import { ReloadOutlined } from "@ant-design/icons";
import CoefficientToggle from "@components/CoefficientToggle";
import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import PeriodFilter from "@components/PeriodFilter";
import { useObjectStore } from "@shared/store/objectStore";
import { Button, Radio, Segmented, Select } from "antd";
import type { DefaultOptionType } from "antd/es/select";

import type { NetworkTelemetryMetric, NetworkTelemetryScope } from "./store";
import { useNetworkTelemetryStore } from "./store";

export type ChannelOption = { label: string; value: number };
export type GroupOption = { label: string; value: string };

const scopeOptions: Array<{ label: string; value: NetworkTelemetryScope }> = [
  { label: "Объект", value: "object" },
  { label: "Канал", value: "channel" },
  { label: "Группа", value: "group" },
];

const metricOptions: Array<{ label: string; value: NetworkTelemetryMetric }> = [
  { label: "kW", value: "kw" },
  { label: "kvar", value: "kvar" },
  { label: "I", value: "i" },
];

const intervalOptions = [
  { label: "5 мин", value: 5 },
  { label: "15 мин", value: 15 },
  { label: "30 мин", value: 30 },
  { label: "60 мин", value: 60 },
];

type Props = {
  objects: Array<{ syb_rnk: number; n_ob: number; txt: string }>;
  objectsLoading: boolean;
  channelsLoading: boolean;
  channelOptions: ChannelOption[];
  groupsLoading: boolean;
  groupOptions: GroupOption[];
  groupChannelOptions?: ChannelOption[];
  ready: boolean;
  loading: boolean;
  onReload: () => void;
};

const NetworkTelemetryFilters = ({
  objects,
  objectsLoading,
  channelsLoading,
  channelOptions,
  groupsLoading,
  groupOptions,
  groupChannelOptions,
  ready,
  loading,
  onReload,
}: Props) => {
  const { selectedObject, setSelectedObject } = useObjectStore();
  const {
    scope,
    setScope,
    metric,
    setMetric,
    interval,
    setInterval,
    coefficient,
    setCoefficient,
    selectedChannel,
    setSelectedChannel,
    selectedGroup,
    setSelectedGroup,
    selectedGroupChannel,
    setSelectedGroupChannel,
    period,
    setPeriod,
    resetChannels,
  } = useNetworkTelemetryStore();

  return (
    <FilterBar>
      <FilterRow>
        <Segmented
          value={scope}
          onChange={(value) => setScope(value as NetworkTelemetryScope)}
          options={scopeOptions}
        />
        <ObjectSelect
          objects={objects}
          loading={objectsLoading}
          value={selectedObject}
          onChange={(value) => {
            setSelectedObject(value);
            resetChannels();
          }}
          style={{ minWidth: 320, flex: 1 }}
        />
        {scope === "channel" && (
          <Select
            showSearch
            placeholder="Канал"
            disabled={!selectedObject}
            loading={channelsLoading}
            options={channelOptions as DefaultOptionType[]}
            value={selectedChannel}
            onChange={setSelectedChannel}
            optionFilterProp="label"
            style={{ minWidth: 280, maxWidth: 520 }}
          />
        )}
        {scope === "group" && (
          <Select
            showSearch
            placeholder="Группа"
            disabled={!selectedObject}
            loading={groupsLoading}
            options={groupOptions as DefaultOptionType[]}
            value={selectedGroup}
            onChange={setSelectedGroup}
            optionFilterProp="label"
            style={{ minWidth: 280, maxWidth: 520 }}
            notFoundContent={groupsLoading ? "Загрузка..." : "Нет групп для объекта"}
          />
        )}
        {scope === "group" && (
          <Select
            showSearch
            allowClear
            placeholder="каналы группы"
            disabled={!selectedGroup || (groupChannelOptions?.length ?? 0) === 0}
            options={(groupChannelOptions ?? []) as DefaultOptionType[]}
            value={selectedGroupChannel ?? undefined}
            onChange={(value) => setSelectedGroupChannel(value ?? null)}
            optionFilterProp="label"
            style={{ minWidth: 200, maxWidth: 320 }}
            notFoundContent="Нет каналов в группе"
          />
        )}
        <Select
          options={intervalOptions}
          value={interval}
          onChange={setInterval}
          style={{ width: 140 }}
        />
        <Button
          aria-label="Обновить"
          title="Обновить"
          icon={<ReloadOutlined aria-hidden />}
          disabled={!ready}
          loading={loading}
          onClick={onReload}
        />
      </FilterRow>
      <FilterRow>
        <Radio.Group
          value={metric}
          onChange={(e) => setMetric(e.target.value)}
          options={metricOptions}
          optionType="button"
          buttonStyle="solid"
        />
        <CoefficientToggle value={coefficient} onChange={setCoefficient} />
        <PeriodFilter
          value={period}
          onChange={setPeriod}
          showMonthControls={false}
          showDayControls
          showRangeControls
          compact
        />
      </FilterRow>
    </FilterBar>
  );
};

export default NetworkTelemetryFilters;
